import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HoverVideoPlayer from './HoverVideoPlayer';
import { API_BASE_URL, MEDIA_BASE_URL } from '../../api';
import { AnimatePresence, motion } from 'framer-motion';
import { Pencil, Film, Sparkles } from 'lucide-react';

interface ClipCardProps {
  id: string;
  title?: string | null;
  thumbnailPath?: string | null;
  filePath?: string | null;
  jobId?: string | null;
  llmOutputId?: string | null;
  type: 'original' | 'edited';
  isSelected?: boolean; // Toplu silme için
  onSelect?: (id: string) => void; // Toplu silme için
  onCardClick?: () => void; // Kartın kendisine tıklama olayı için
}

const ClipCard: React.FC<ClipCardProps> = ({
  id,
  title,
  thumbnailPath,
  filePath,
  jobId,
  llmOutputId,
  type,
  isSelected,
  onSelect,
  onCardClick,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const touchTimerRef = useRef<NodeJS.Timeout | null>(null);
  // filePath kontrolü
  if (!filePath || filePath.startsWith('error/')) {
    console.warn(`[ClipCard] Invalid or missing filePath for clip ID ${id}: "${filePath}". Not rendering ClipCard.`);
    return null;
  }

  const [isHovering, setIsHovering] = useState(false);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset imageError if the thumbnailPath prop changes
  useEffect(() => {
    setImageError(false);
  }, [thumbnailPath]);

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHovering(false);
    }
  };

  // Mobile touch handlers
  const handleTouchStart = () => {
    if (isMobile && !showMobilePreview) {
      touchTimerRef.current = setTimeout(() => {
        setShowMobilePreview(true);
      }, 500); // Long press for 500ms
    }
  };

  const handleTouchEnd = () => {
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  };

  const handleTouchCancel = () => {
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
    setShowMobilePreview(false);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (touchTimerRef.current) {
        clearTimeout(touchTimerRef.current);
      }
    };
  }, []);

  // Kartın kendisine tıklama olayı, popup açmak için ClipGalleryPage'e iletilecek
  // Eğer düzenleme modundaysak ve onSelect varsa, seçimi değiştirmeli, popup açmamalı.
  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // Prevent card click if edit button or checkbox is clicked
    if (target.closest('.clip-select-checkbox-container') || target.closest('.edit-button')) {
      return;
    }

    if (onSelect && isSelected !== undefined) {
      onSelect(id);
    } else if (onCardClick) {
      onCardClick();
    }
  };

  const handleEditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Prevent card click event
    if (jobId && filePath) {
        const fileName = filePath.split('/').pop();
        if (!fileName) {
            alert('Could not determine the file name from the video source.');
            return;
        }
        // Use the correct streaming URL for the editor
        const fullVideoUrlForEditor = `${MEDIA_BASE_URL}/api/jobs/clips/file/${jobId}/${fileName}`;
        const queryParams = new URLSearchParams({
            jobId: jobId,
            clipId: id, // Pass clipId to the editor
            videoUrl: fullVideoUrlForEditor,
            returnUrl: window.location.href,
        }).toString();
        
        // Assuming the editor is hosted at a specific URL, potentially configurable
        const editorBaseUrl = import.meta.env['VITE_EDITOR_URL'] || 'https://editor.clipoid.com';
        const fullEditorUrl = `${editorBaseUrl}?${queryParams}`;
        
        window.open(fullEditorUrl, '_blank');
    } else {
        alert('Clip information is missing, cannot open editor.');
    }
  };


  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation(); // Kartın tıklama olayının tetiklenmesini engelle
    if (onSelect) {
      onSelect(id);
    }
  };

  const actualThumbnailSrc = thumbnailPath ? `${MEDIA_BASE_URL}/media/${thumbnailPath.replace(/^\/+/, '').replace(/^data\/storage_content\//, '')}` : null;
  const fileName = filePath ? filePath.split('/').pop() : null;
  // Use different endpoint for edited clips
  const fullVideoPath = jobId && fileName ? 
    (type === 'edited' ? `${MEDIA_BASE_URL}/api/jobs/clips/edited/${jobId}/${fileName}` : `${MEDIA_BASE_URL}/api/jobs/clips/file/${jobId}/${fileName}`) 
    : null;

  // Debug logging disabled to prevent console spam
  // useEffect(() => {
  //   if (process.env.NODE_ENV === 'development') {
  //     if (type === 'edited') {
  //       console.log(`[ClipCard - Edited] ID: ${id}, Original thumbnailPath: ${thumbnailPath}, Actual thumbnail src: ${actualThumbnailSrc}`);
  //     } else if (type === 'original') {
  //       console.log(`[ClipCard - Original] ID: ${id}, Original thumbnailPath: ${thumbnailPath}, Actual thumbnail src: ${actualThumbnailSrc}`);
  //     }
  //   }
  // }, [id, thumbnailPath, actualThumbnailSrc, type]);

  // Edited klipler için 9:16 en boy oranı sınıfı
  const aspectRatioClass = type === 'edited' ? 'aspect-9-16' : 'aspect-video';

  return (
    <div className="relative group">
      {/* Background gradient glow */}
      <div className={`
        absolute -inset-0.5 rounded-2xl opacity-0 blur-xl transition-opacity duration-300
        ${isSelected 
          ? 'bg-gradient-to-r from-blue-500/40 to-cyan-500/40 opacity-60' 
          : 'bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 group-hover:opacity-30'
        }
      `} />
      
      <div
        className={`
          clip-card relative bg-black/30 backdrop-blur-xl border rounded-2xl 
          shadow-lg overflow-hidden transform transition-all duration-300
          hover:bg-black/40 hover:scale-[1.02] cursor-pointer
          ${isSelected 
            ? 'border-blue-500/60 bg-black/40' 
            : 'border-white/10 hover:border-white/20'
          }
        `}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        aria-label={`View clip: ${title || 'Untitled Clip'}`}
      >
        {/* Selection Checkbox */}
        {onSelect && isSelected !== undefined && (
          <div className="clip-select-checkbox-container absolute top-3 right-3 z-40">
            <div className="bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg p-1">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={handleCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                className="w-4 h-4 text-blue-500 bg-black/20 border-white/30 rounded focus:ring-blue-500 focus:ring-offset-0"
                aria-label={`Select clip ${title || 'Untitled Clip'}`}
              />
            </div>
          </div>
        )}
        
        {/* Thumbnail Section */}
        <div className={`relative ${aspectRatioClass} bg-black/20 overflow-hidden`}>
          {actualThumbnailSrc && !imageError ? (
            <img
              src={actualThumbnailSrc}
              alt={title || 'Clip thumbnail'}
              className="w-full h-full object-cover absolute inset-0 z-10"
              onError={() => {
                console.warn(`[ClipCard] Error loading thumbnail: ${actualThumbnailSrc}. Thumbnail will not be displayed. ID: ${id}`);
                setImageError(true);
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Film size={32} className="text-white/40" />
            </div>
          )}
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
          
          {/* Hover/Touch Video Player */}
          <AnimatePresence>
            {(isHovering || showMobilePreview) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 z-20 bg-black/50 flex items-center justify-center"
                onClick={(e) => {
                  if (showMobilePreview) {
                    e.stopPropagation();
                    setShowMobilePreview(false);
                  }
                }}
              >
                {fullVideoPath && <HoverVideoPlayer key={`hover-${id}`} src={fullVideoPath} />}
                {showMobilePreview && (
                  <div className="absolute top-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-xs">
                    Tap to close
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Edit Button - Always visible on mobile */}
          <AnimatePresence>
            {(isHovering || (isMobile && type === 'original')) && type === 'original' && (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2, delay: 0.1 }}
                className="
                  edit-button absolute bottom-3 right-3 z-30 
                  bg-gradient-to-r from-blue-500 to-cyan-500 text-white 
                  rounded-full p-2.5 shadow-lg hover:from-blue-600 hover:to-cyan-600 
                  transition-all duration-200 transform hover:scale-110
                "
                onClick={handleEditClick}
                aria-label="Edit clip"
              >
                <Pencil size={16} />
              </motion.button>
            )}
          </AnimatePresence>
          
          {/* Type badge */}
          {type === 'edited' && (
            <div className="absolute top-3 left-3 z-30">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                <Sparkles size={10} />
                Edited
              </div>
            </div>
          )}
        </div>
        
        {/* Content Section */}
        <div className="p-4">
          <h3 
            className="text-sm font-semibold text-white truncate leading-tight" 
            title={title || 'Untitled Clip'}
          >
            {title || 'Untitled Clip'}
          </h3>
          
          {/* Additional info could go here */}
          <div className="mt-2 flex items-center justify-between">
            <div className="text-xs text-white/50">
              {type === 'original' ? 'AI Generated' : 'Custom Edit'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClipCard;